// Copyright (c) StrangeBee 2024

import React, { ReactNode, ReactElement } from 'react';
import { classList } from '@Helpers/className';
import { useListAllUsers } from '@Queries/userQueries';
import { AuditOutput, OperationType } from 'thehive-sdk';
import { useStateValue } from '@Context/StateProvider';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { SystemUser } from '@Constants/organisation';
import { findUser } from '@Helpers/findUser';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { cutString } from '@Helpers/string';
import { useLocalMessage } from '@Helpers/locale';
import { ICONS } from '@Constants/iconsApp';
import { useFormatDate } from '@Hooks/useFormatDate';

import './StreamCard.scss';

interface LinkParentProps {
    target: any;
    data: any;
}

const LinkCase = ({ target, data }: LinkParentProps): ReactElement => {
    const path = RouteBuilder.compute('cases.details', { caseId: data?._id });
    return (
        <Link target={target} className="stream-footer__link" to={path}>
            <ICONS.CaseIcon /> {cutString(`${data?.number} - ${data?.title}`, 20)}
        </Link>
    );
};

const LinkAlert = ({ target, data }: LinkParentProps): ReactElement => {
    const path = RouteBuilder.compute('alerts.details', { alertId: data?._id });
    return (
        <Link target={target} className="stream-footer__link" to={path}>
            <ICONS.AlertIcon /> {cutString(data?.title, 20)}
        </Link>
    );
};

const LinkOrganisation = ({ target, data }: LinkParentProps): ReactElement => {
    const path = RouteBuilder.compute('organisation.caseTemplates', { orgId: data?.name });
    return (
        <Link target={target} className="stream-footer__link" to={path}>
            <ICONS.OrganisationIcon /> {cutString(data?.name, 20)}
        </Link>
    );
};

const libLinkParent: Record<string, any> = {
    case: LinkCase,
    alert: LinkAlert,
    organisation: LinkOrganisation,
};

const LinkParent = ({ target, data }: LinkParentProps): ReactElement | null => {
    const RealLink = libLinkParent[data?._type.toLowerCase()] || null;

    return RealLink ? <RealLink target={target} data={data} /> : null;
};

interface StreamCardProps {
    base: AuditOutput['base'];
    title: string;
    parent?: any;
    className?: string;
    information?: string;
    children?: ReactNode;
    icon?: ReactNode;
    path?: string | null;
}

const StreamCard = ({
    base,
    className,
    children,
    path,
    title,
    information,
    icon = null,
    parent,
}: StreamCardProps): ReactElement => {
    const local = useLocalMessage();
    const [{ targetWindow }] = useStateValue();
    const { _createdAt = 0, _createdBy = '', action = 'update', objectType } = base;
    const f = useFormatDate();

    const actionStatus = `status-${action}`.toLowerCase();
    const { data: allUsersList = [] } = useListAllUsers();
    const author = _createdBy !== SystemUser.login ? findUser(_createdBy, allUsersList) : SystemUser;

    const { name = '?' } = author || {};

    const listAction: Record<OperationType, string> = {
        delete: local('stream.header.delete'), //'Deleted by ',
        create: local('stream.header.creation'), //'Created by ',
        update: local('stream.header.update'), //'Updated by ',
        merge: local('stream.header.merge'),
    };

    const listIcon: Record<OperationType, ReactElement> = {
        delete: <ICONS.MinusCircleOutlined />,
        create: <ICONS.PlusCircleOutlined />,
        merge: <ICONS.MergeIcon />,
        update: <ICONS.InfoCircleOutlined />,
    };

    return (
        <div className={classList('stream-card', className, actionStatus)}>
            <header className="stream-card__header">
                <div className="stream-header__user">
                    <span className="stream-header__user-icon">{listIcon[action]}</span>
                    {listAction[action]} <strong className="stream-header__user-name">{name}</strong>
                </div>
                <div className="stream-header__date">{f(_createdAt)}</div>
            </header>
            <div className="stream-card__content">
                {icon ? (
                    <THTooltip placement="top" title={information || objectType}>
                        <div className="stream-content__icon">{icon}</div>
                    </THTooltip>
                ) : null}
                <div className="stream-content__wrapper">
                    {path && action !== 'delete' ? (
                        <Link to={path} target={targetWindow} className="stream-header__link">
                            {title}
                        </Link>
                    ) : (
                        <span className="stream-header__plain">{title}</span>
                    )}
                    <div className="stream-body">{children}</div>
                    {parent && parent?._id ? (
                        <div className={classList('stream-footer', { 'footer-delete': action === 'delete' })}>
                            <LinkParent target={targetWindow} data={parent} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default StreamCard;
